import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Lock,
  CreditCard,
  Bell,
  ShieldAlert,
  UserCheck,
  KeyRound,
  Network,
  Activity,
  Globe2,
  Headphones,
  Route as RouteIcon,
  Gauge,
  Clock,
  SignalHigh,
  BadgeCheck,
  Layers,
  Eye,
  CheckCircle,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import FlatIcon from "@/components/site/FlatIcon";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import CTASection from "@/components/site/CTASection";
import Seo from "@/components/Seo";

const whyItMatters = [
  {
    title: "OTP and Authentication",
    description:
      "One-time passcodes sent over SMS remain the most widely reachable way to confirm that the person signing in, approving a payment, or resetting a password is the account holder.",
    Icon: Lock,
    color: "text-amber",
  },
  {
    title: "Transaction Confirmations",
    description:
      "A short message confirming that a card payment, transfer, or top-up went through gives customers immediate certainty and reduces inbound support contacts.",
    Icon: CreditCard,
    color: "text-indigo",
  },
  {
    title: "Payment Alerts",
    description:
      "Incoming payments, failed direct debits, low balances, and settlement updates are time-sensitive by nature and are read fastest as a text message.",
    Icon: Bell,
    color: "text-sky",
  },
  {
    title: "Login and Security Alerts",
    description:
      "Notify customers when a new device signs in, a password changes, or account details are updated, so unauthorised access is spotted quickly.",
    Icon: KeyRound,
    color: "text-violet",
  },
  {
    title: "Account Notifications",
    description:
      "Statement availability, card delivery, limit changes, and onboarding progress updates keep customers informed without relying on push notifications alone.",
    Icon: Layers,
    color: "text-lime",
  },
  {
    title: "Fraud Prevention and KYC",
    description:
      "Step-up verification and suspicious-activity warnings support fraud teams, while verification messages help move customers through KYC and onboarding checks.",
    Icon: ShieldAlert,
    color: "text-rose",
  },
];

const challenges = [
  {
    title: "Delayed OTPs",
    description:
      "When a passcode arrives after the entry window has closed, the customer retries or abandons the flow altogether. Latency, not just delivery, decides whether authentication works.",
    Icon: Clock,
    color: "text-amber",
  },
  {
    title: "Failed Delivery",
    description:
      "Messages that never reach the handset create support tickets, drop-offs at sign-up, and payments that stall halfway through approval.",
    Icon: SignalHigh,
    color: "text-rose",
  },
  {
    title: "Inconsistent International Routes",
    description:
      "A route that performs well in one market can behave very differently in another. Fintechs operating across borders often see delivery quality vary country by country.",
    Icon: Globe2,
    color: "text-sky",
  },
  {
    title: "Sender ID Issues",
    description:
      "Sender ID rules differ between countries. Where they are registered, pre-approved, or overwritten, brand recognition and deliverability can both be affected.",
    Icon: BadgeCheck,
    color: "text-indigo",
  },
  {
    title: "Grey and Indirect Routing",
    description:
      "Traffic passed through long chains of intermediaries is harder to troubleshoot, less predictable, and more exposed to filtering along the way.",
    Icon: RouteIcon,
    color: "text-violet",
  },
  {
    title: "Limited Delivery Visibility",
    description:
      "Without clear delivery reporting, teams cannot tell whether an authentication problem sits in their application, their provider, or the destination network.",
    Icon: Eye,
    color: "text-lime",
  },
];

const howWeHelp = [
  {
    title: "Telecom-Native Expertise",
    description:
      "Yootelco comes from the telecommunications side of messaging, with more than 18 years of experience in telecoms and digital communications. That background shapes how we build and troubleshoot fintech traffic.",
    Icon: Network,
    color: "text-indigo",
  },
  {
    title: "Routing Quality",
    description:
      "We work with direct connections to mobile network operators so that authentication and transactional traffic follows routes chosen for reliability rather than lowest cost.",
    Icon: RouteIcon,
    color: "text-amber",
  },
  {
    title: "Delivery Monitoring",
    description:
      "Traffic is monitored so that performance changes are noticed and acted on, and so your team has delivery insight to work from when investigating an issue.",
    Icon: Activity,
    color: "text-sky",
  },
  {
    title: "Global Connectivity",
    description:
      "International messaging is delivered through operator and carrier connectivity across the markets our clients serve, supporting consistent reach as you expand.",
    Icon: Globe2,
    color: "text-lime",
  },
  {
    title: "Operational Support",
    description:
      "A UK-based team built for enterprise and regulated clients works with your engineering and operations teams rather than routing you through a ticket queue.",
    Icon: Headphones,
    color: "text-violet",
  },
  {
    title: "Flexible Routing and Fast Resolution",
    description:
      "Routing can be adjusted per destination and per traffic type, and when something looks wrong on a route we investigate and change it rather than leaving it in place.",
    Icon: Gauge,
    color: "text-rose",
  },
];

const useCases = [
  {
    title: "OTP Authentication",
    description:
      "Deliver one-time passcodes for sign-in, password resets, device changes, and payment approvals, where speed and delivery both decide whether the flow completes.",
    Icon: Lock,
    color: "text-amber",
    href: "/use-cases/otp-verification",
    linkLabel: "OTP verification",
  },
  {
    title: "Payment Notifications",
    description:
      "Confirm payments, transfers, refunds, and settlements so customers and merchants know where money is without opening the app.",
    Icon: CreditCard,
    color: "text-indigo",
    href: "/use-cases/payment-fintech",
    linkLabel: "Payment and fintech messaging",
  },
  {
    title: "Transaction Alerts",
    description:
      "Send card usage, balance, and account activity alerts as they happen, using transactional routes intended for time-critical messages.",
    Icon: Bell,
    color: "text-sky",
    href: "/use-cases/transactional-alerts",
    linkLabel: "Transactional alerts",
  },
  {
    title: "Fraud Prevention",
    description:
      "Warn customers about suspicious logins or unusual transactions, and let them confirm or reject activity before losses grow.",
    Icon: ShieldAlert,
    color: "text-rose",
    href: "/use-cases/fraud-prevention",
    linkLabel: "Fraud prevention",
  },
  {
    title: "Identity Verification",
    description:
      "Support onboarding and KYC journeys with verification messages that confirm a customer controls the phone number on the application.",
    Icon: UserCheck,
    color: "text-lime",
    href: "/use-cases/identity-verification",
    linkLabel: "Identity verification",
  },
  {
    title: "Account Security",
    description:
      "Add a second factor to sensitive actions such as changing payout details, adding a beneficiary, or raising a transaction limit.",
    Icon: KeyRound,
    color: "text-violet",
    href: "/solutions/two-factor-authentication",
    linkLabel: "Two-factor authentication",
  },
];

const deliveryFactors = [
  {
    title: "Delivery Rate",
    description:
      "For a marketing campaign, an undelivered message is a lost impression. For a fintech OTP, it is a customer who cannot log in or complete a payment.",
    Icon: SignalHigh,
    color: "text-amber",
  },
  {
    title: "Latency",
    description:
      "Authentication codes are only useful inside a short window. Consistent delivery within seconds matters as much as the overall delivery percentage.",
    Icon: Clock,
    color: "text-sky",
  },
  {
    title: "Route Quality",
    description:
      "Direct, well-understood routes behave predictably. Long intermediary chains introduce variability that is difficult to diagnose after the fact.",
    Icon: RouteIcon,
    color: "text-indigo",
  },
  {
    title: "Sender ID",
    description:
      "A recognisable, correctly registered sender helps customers trust the message and helps the message reach the handset in markets with registration rules.",
    Icon: BadgeCheck,
    color: "text-violet",
  },
  {
    title: "Redundancy",
    description:
      "Alternative routing paths matter when a network or connection degrades, so authentication traffic is not dependent on a single option.",
    Icon: Layers,
    color: "text-lime",
  },
  {
    title: "Monitoring",
    description:
      "Delivery visibility turns a vague complaint about OTPs into a specific destination, operator, and time window your team can act on.",
    Icon: Activity,
    color: "text-rose",
  },
];

const faqs = [
  {
    question: "What is SMS messaging used for in fintech?",
    answer:
      "Fintech companies use SMS mainly for authentication and transactional communication: one-time passcodes for login and payment approval, transaction and payment confirmations, balance and account activity alerts, security notifications, fraud warnings, and verification messages during onboarding or KYC. These messages are time-sensitive and need to reach the customer reliably, which is why they are usually sent over A2P SMS rather than in-app channels alone.",
  },
  {
    question: "How does OTP SMS work for fintech apps?",
    answer:
      "The application generates a short, single-use code and submits it through an SMS API or messaging platform. The message is routed to the customer's mobile network and delivered to their handset, and the customer enters the code back into the app to complete sign-in, a payment approval, or a sensitive account change. Because the code expires quickly, both delivery success and delivery speed determine whether the authentication flow works.",
  },
  {
    question: "Why do fintech OTP messages fail?",
    answer:
      "Common causes include poor or indirect routing to the destination network, carrier filtering, sender ID rules that are not met in a particular country, latency that pushes the message past the code's expiry window, and unreachable or ported numbers. Without delivery reporting it is often unclear which of these applies, which is why route quality and monitoring matter as much as the message content.",
  },
  {
    question: "How can fintech companies improve SMS delivery rates?",
    answer:
      "Use routes intended for transactional traffic rather than the cheapest available option, work with a provider that has direct operator connectivity in your key markets, register sender IDs where local rules require it, keep authentication messages short and clear, monitor delivery per destination and operator, and make sure alternative routing is available if a path degrades.",
  },
  {
    question: "What is the difference between transactional SMS and promotional SMS?",
    answer:
      "Transactional SMS is triggered by a customer action or an account event, such as an OTP, a payment confirmation, or a fraud alert, and is expected by the recipient. Promotional SMS markets products or offers and generally requires prior consent under marketing and data protection rules. The two are usually treated differently by regulators and by mobile networks, and transactional traffic is normally routed with speed and reliability as the priority.",
  },
  {
    question: "How can fintech companies send SMS internationally?",
    answer:
      "International sending relies on a provider with connectivity into the mobile networks of each destination country, along with awareness of local sender ID and content rules. Rather than treating every market the same, routing should be configured per destination, monitored, and adjusted as network conditions and regulations change.",
  },
];

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "SMS Messaging for Fintech Companies",
  serviceType: "A2P SMS messaging",
  description:
    "A2P SMS, OTP, and transactional messaging for fintech companies, covering authentication, payment notifications, transaction alerts, fraud prevention, and international delivery.",
  provider: {
    "@type": "Organization",
    name: "Yootelco",
    url: "https://yootelco.com",
  },
  areaServed: "Worldwide",
  audience: {
    "@type": "Audience",
    audienceType: "Fintech companies, payment platforms and financial services providers",
  },
  url: "https://yootelco.com/industries/fintechs",
};

const IndustriesFintechs = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <main className="industries-page min-h-screen bg-white">
      <Seo
        title="SMS for Fintech Companies | Secure A2P Messaging | Yootelco"
        description="Reliable SMS for fintech: OTP authentication, transaction alerts, payment notifications and fraud prevention with quality routing and global delivery. Talk to Yootelco."
        path="/industries/fintechs"
        faqs={faqs}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>
      <SiteHeader />

      {/* Hero */}
      <section className="pb-16 pt-[120px] md:pb-20 md:pt-[140px]" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="container">
          <div className="mx-auto max-w-[800px] text-center">
            <span className="inline-block rounded-full bg-primary/15 px-4 py-1.5 text-[13px] font-bold uppercase tracking-wider text-foreground">
              Messaging for Fintech
            </span>
            <h1 className="mt-5 font-display text-[44px] font-bold leading-[1.05] tracking-tight text-foreground md:text-[60px] [text-wrap:balance]">
              SMS Messaging for Fintech Companies
            </h1>
            <p className="mx-auto mt-[18px] max-w-[700px] text-[15px] font-medium leading-[1.55] text-muted-foreground md:text-[16px] [text-wrap:balance]">
              Yootelco helps fintech companies deliver authentication, transaction and security messages reliably across the markets they operate in. From OTP SMS and payment notifications to fraud alerts, we route financial messaging through carrier connections chosen for delivery quality and speed.
            </p>
            <Link
              to="/contact"
              className="mt-[28px] inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
            >
              Contact Sales
            </Link>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-5">
              {[
                { label: "A2P SMS", Icon: SignalHigh },
                { label: "OTP SMS", Icon: Lock },
                { label: "Transactional SMS", Icon: Bell },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2 text-[13px] font-semibold text-muted-foreground">
                  <item.Icon className="h-4 w-4 text-primary" strokeWidth={2.2} />
                  {item.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why SMS Matters for Fintech */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">Why SMS Matters for Fintech</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Financial products run on trust, and much of that trust is built through small, timely messages. A fintech customer approving a payment, signing in from a new device, or completing onboarding is waiting on a text message at that moment. If it arrives late or not at all, the payment stalls, the sign-up is abandoned, or a support ticket is created.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            SMS is used for this because it does not depend on an app being installed, a push notification being enabled, or a data connection being available. That reach is exactly why fintechs, payment platforms and financial services providers use{" "}
            <Link to="/solutions/a2p-sms" className="font-semibold text-primary underline-offset-4 hover:underline">
              A2P SMS
            </Link>{" "}
            for authentication and transactional communication rather than marketing alone.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {whyItMatters.map((item) => (
            <article
              key={item.title}
              className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <FlatIcon Icon={item.Icon} color={item.color} />
              <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Fintech SMS Challenges */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">Fintech SMS Challenges</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Most fintech messaging problems are not caused by the message itself. They come from how traffic is routed, how each destination network treats it, and how much visibility the sending team has when something changes. These are the issues fintech engineering and operations teams raise most often.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {challenges.map((item) => (
            <article
              key={item.title}
              className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <FlatIcon Icon={item.Icon} color={item.color} />
              <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* How Yootelco Helps */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">How Yootelco Helps</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Yootelco works with fintechs, payment providers and financial institutions on the messaging that has to work: verification, confirmation and security. Our approach is built around route quality, visibility and hands-on support rather than volume for its own sake.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {howWeHelp.map((item) => (
            <article
              key={item.title}
              className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <FlatIcon Icon={item.Icon} color={item.color} />
              <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
        <div className="mx-auto mt-[28px] max-w-[860px] text-center">
          <p className="text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Where a fintech also needs to reach customers by phone, for example for high-risk transaction confirmation or customer support, Yootelco supports{" "}
            <Link to="/solutions/voice-termination" className="font-semibold text-primary underline-offset-4 hover:underline">
              voice termination
            </Link>{" "}
            as a secondary channel alongside messaging.
          </p>
        </div>
      </section>

      {/* Fintech Use Cases */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Fintech Use Cases</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            The messaging needs of a payments platform, a lending app and a digital bank overlap more than they differ. These are the use cases fintech teams bring to us most often.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {useCases.map((item) => (
            <article
              key={item.title}
              className="flex flex-col rounded-[18px] border border-border/60 bg-card px-6 py-7 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <FlatIcon Icon={item.Icon} color={item.color} className="mx-auto block w-fit" />
              <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] flex-1 text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
              <Link
                to={item.href}
                className="mt-4 text-[13px] font-bold text-primary underline-offset-4 hover:underline"
              >
                {item.linkLabel}
              </Link>
            </article>
          ))}
        </div>
      </section>

      {/* Why Delivery Quality Matters */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">Why Delivery Quality Matters</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            In fintech, a message that does not arrive is not a missed impression, it is a blocked customer action. Delivery quality is therefore an operational and security concern, not just a messaging metric, and it is measured across several dimensions at once.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {deliveryFactors.map((item) => (
            <article
              key={item.title}
              className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <FlatIcon Icon={item.Icon} color={item.color} />
              <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
        <div className="mx-auto mt-[28px] max-w-[860px] text-center">
          <p className="text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            This is why Yootelco positions around delivery assurance: the goal is that every authentication and transactional message a fintech sends behaves the same way today as it did yesterday, in every market it is sent to. You can read more about how we approach{" "}
            <Link to="/coverage-routes" className="font-semibold text-primary underline-offset-4 hover:underline">
              coverage and routing
            </Link>
            .
          </p>
        </div>
      </section>

      {/* Global Fintech Messaging */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">Global Fintech Messaging</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Fintech rarely stays in one market for long. A payments platform adds a corridor, a lending app opens a new country, a wallet starts serving customers who travel. Each new market brings its own mobile networks, sender ID expectations, content rules and traffic conditions, and messaging that performed well at home does not automatically perform well abroad.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Local routing and direct carrier connectivity matter because they shorten the path between your application and the customer's handset. Fewer intermediaries means fewer places for a message to be delayed, filtered or altered, and clearer answers when a specific destination starts behaving differently. Yootelco configures routing per destination and per traffic type, so authentication messages are treated as time-critical wherever they are sent.
          </p>
          <div className="mt-[24px] mx-auto max-w-[640px] text-left">
            {[
              "Direct connections to mobile network operators",
              "Routing configured per destination and traffic type",
              "Delivery monitoring across international routes",
              "Support for sender ID requirements where local rules apply",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 py-3">
                <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-primary/10 text-primary">
                  <CheckCircle className="h-3.5 w-3.5" strokeWidth={2.5} />
                </span>
                <p className="text-[15px] leading-[1.55] text-muted-foreground">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        title="Ready to Improve Your Fintech Messaging?"
        description="Tell us about your authentication and transactional SMS requirements and we'll show you how Yootelco can support them."
        useCase="Fintechs"
      />

      {/* FAQ */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Frequently Asked Questions</h2>
        </div>
        <div className="mx-auto mt-10 max-w-[1120px]">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((item, index) => (
              <AccordionItem key={item.question} value={`item-${index}`} className="border-b border-border">
                <AccordionTrigger className="py-6 text-left text-lg font-bold hover:no-underline md:text-xl">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-6 pr-12 text-base leading-7 text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
};

export default IndustriesFintechs;
